import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { AlertCircle, LayoutGrid, RefreshCw } from 'lucide-react';
import { clsx } from 'clsx';
import { Spinner, useToast } from '@centraoneplus/ui';
import { pipelineService } from '@/services/pipeline.service';
import { ticketService } from '@/services/ticket.service';
import { useWebSocket } from '@/hooks/useWebSocket';
import { BoardColumn } from './components/BoardColumn';
import { TicketKanbanCard } from './components/TicketKanbanCard';
import { pendingMoves } from './pendingMoves';
import { TicketToolbar } from '@components/tickets/TicketToolbar';
import { buildApiFilters, type TicketFilters } from '@components/tickets/AdvancedFilters';
import { useAuthStore } from '@stores/authStore';
import { pipelinesSettingsTarget } from '../settings/pipelines-target';
import { formatters, type ExportColumn } from '@utils/export';
import type { Pipeline } from '@/types/pipeline';
import type { Ticket, TicketStatus } from '@centraoneplus/shared-types';
import { rememberTicketsView } from '@/lib/lastTicketsView';

const TICKETS_PER_STAGE = 10;

/** The floating shell: cards sit ON the canvas, they do not butt against it. */
const CARD: React.CSSProperties = { borderRadius: 10, boxShadow: 'var(--co-shadow-card)' };
const PRIMARY =
  'flex h-[34px] flex-none items-center justify-center rounded-lg bg-[var(--co-brass-ink)] ' +
  'px-[14px] text-[12.5px] font-bold text-[var(--co-brass-on)] hover:opacity-90';

interface StageState {
  tickets: Ticket[];
  total: number;
  page: number;
  hasMore: boolean;
  isLoading: boolean;
}

// Board state helpers. Centralized to guarantee a ticket never appears in more
// than one stage bucket — the root cause of the "ticket duplicated across lanes"
// bug after drag + navigate-back was stale optimistic state leaving a copy in
// the source lane while a refetch/WS event also re-added it to the target.
function removeTicketFromAllStages(
  prev: Record<string, StageState>,
  ticketId: string,
): { state: Record<string, StageState>; found: Ticket | undefined } {
  const next: Record<string, StageState> = {};
  let found: Ticket | undefined;
  for (const [sid, s] of Object.entries(prev)) {
    const match = s.tickets.find((t) => t.id === ticketId);
    if (match) {
      if (!found) found = match;
      next[sid] = {
        ...s,
        tickets: s.tickets.filter((t) => t.id !== ticketId),
        total: Math.max(0, s.total - 1),
      };
    } else {
      next[sid] = s;
    }
  }
  return { state: next, found };
}

function placeTicketInStage(
  prev: Record<string, StageState>,
  ticketId: string,
  targetStageId: string,
  opts: { ticketIfMissing?: Ticket; patch?: Partial<Ticket> } = {},
): Record<string, StageState> {
  const { state: cleaned, found } = removeTicketFromAllStages(prev, ticketId);
  const tgt = cleaned[targetStageId];
  if (!tgt) return prev; // target lane not rendered — leave state untouched
  const base = found ?? opts.ticketIfMissing;
  if (!base) return cleaned; // nothing to insert, but dedup cleanup still stands
  return {
    ...cleaned,
    [targetStageId]: {
      ...tgt,
      tickets: [
        { ...base, stageId: targetStageId, ...opts.patch } as Ticket,
        ...tgt.tickets,
      ],
      total: tgt.total + 1,
    },
  };
}

// Shared export column definitions
const TICKET_EXPORT_COLUMNS: ExportColumn<Ticket>[] = [
  { key: 'ticketNumber', header: 'Ticket #' },
  { key: 'subject', header: 'Subject' },
  { key: 'status', header: 'Status' },
  { key: 'priority', header: 'Priority' },
  { key: 'channel', header: 'Channel' },
  { key: 'contactName', header: 'Customer Name' },
  { key: 'contactEmail', header: 'Customer Email' },
  { key: 'createdAt', header: 'Created', formatter: (value) => formatters.dateTime(value) },
  { key: 'updatedAt', header: 'Updated', formatter: (value) => formatters.dateTime(value) },
  { key: 'tags', header: 'Tags', formatter: (value) => formatters.array(value) },
];

// Remembers the board's last-selected pipeline across navigation so returning
// to the tickets page restores it instead of snapping back to the default.
const SELECTED_PIPELINE_STORAGE_KEY = 'tickets:selectedPipelineId';

export default function TicketsBoardPage() {
  // So a ticket's "← Tickets" link returns to this view.
  useEffect(() => rememberTicketsView('/tickets'), []);
  const navigate = useNavigate();
  const toast = useToast();
  // Current user id — resolves the "Assigned to me" filter to real query params.
  const currentUserId = useAuthStore((s) => s.user?.id);
  const currentTenantId = useAuthStore((s) => s.user?.tenantId);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [selectedPipelineId, setSelectedPipelineId] = useState<string | null>(null);
  const [pipelinesLoading, setPipelinesLoading] = useState(true);
  const [pipelinesError, setPipelinesError] = useState<string | null>(null);
  const [activeTicket, setActiveTicket] = useState<Ticket | null>(null);
  const [isMoving, setIsMoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter & search state
  const [searchQuery, setSearchQuery] = useState('');
  const [advancedFilters, setAdvancedFilters] = useState<TicketFilters>({});
  // Committed search — only updates on submit (Enter key), not on every keystroke
  const [committedSearch, setCommittedSearch] = useState('');
  // Migration state
  const [isMigrating, setIsMigrating] = useState(false);

  // Per-stage ticket state
  const [stageStates, setStageStates] = useState<Record<string, StageState>>({});
  const [initialLoading, setInitialLoading] = useState(false);

  // Refs for stable access in callbacks
  const stageStatesRef = useRef(stageStates);
  stageStatesRef.current = stageStates;
  const selectedPipelineIdRef = useRef(selectedPipelineId);
  selectedPipelineIdRef.current = selectedPipelineId;
  const advancedFiltersRef = useRef(advancedFilters);
  advancedFiltersRef.current = advancedFilters;
  const committedSearchRef = useRef(committedSearch);
  committedSearchRef.current = committedSearch;

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  // Fetch pipelines on mount
  useEffect(() => {
    const loadPipelines = async () => {
      try {
        setPipelinesLoading(true);
        setPipelinesError(null);
        const data = await pipelineService.getPipelines();
        setPipelines(data);
        // Restore the previously-selected pipeline if it still exists; otherwise
        // fall back to the default (or first). The existence check also handles
        // a stale id left over from a different tenant.
        const storedId = localStorage.getItem(SELECTED_PIPELINE_STORAGE_KEY);
        const restored = storedId ? data.find((p) => p.id === storedId) : undefined;
        const initialPipeline = restored || data.find((p) => p.isDefault) || data[0];
        if (initialPipeline) setSelectedPipelineId(initialPipeline.id);
      } catch (err) {
        console.error('Error loading pipelines:', err);
        setPipelinesError('Failed to load pipelines');
      } finally {
        setPipelinesLoading(false);
      }
    };
    loadPipelines();
  }, []);

// In TicketsBoardPage.tsx — replace handleExport
const handleExport = useCallback(async () => {
  const apiFilters = buildApiFilters(advancedFilters, committedSearch, currentUserId);
  const PAGE_SIZE = 100;
  
  // Fetch first page to get total
  const first = await ticketService.getTickets({
    pipelineId: selectedPipelineId!,
    page: 1,
    limit: PAGE_SIZE,
    sortBy: 'updatedAt',
    sortOrder: 'desc',
    ...apiFilters,
  });

  const totalPages = Math.ceil(first.total / PAGE_SIZE);
  
  if (totalPages <= 1) return first.data;

  // Fetch remaining pages in parallel
  const rest = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, i) =>
      ticketService.getTickets({
        pipelineId: selectedPipelineId!,
        page: i + 2,
        limit: PAGE_SIZE,
        sortBy: 'updatedAt',
        sortOrder: 'desc',
        ...apiFilters,
      })
    )
  );

  return [...first.data, ...rest.flatMap((r) => r.data)];
}, [selectedPipelineId, advancedFilters, committedSearch, currentUserId]);

  // Change the active pipeline and remember it for next time the board mounts.
  const handlePipelineChange = useCallback((pipelineId: string) => {
    setSelectedPipelineId(pipelineId);
    localStorage.setItem(SELECTED_PIPELINE_STORAGE_KEY, pipelineId);
  }, []);

  const selectedPipeline = pipelines.find((p) => p.id === selectedPipelineId);
  const stages = useMemo(() => selectedPipeline?.stages || [], [selectedPipeline]);
  const stagesRef = useRef(stages);
  stagesRef.current = stages;

  // WebSocket for real-time updates
  const { onTicketCreated, onTicketUpdated } = useWebSocket();

  // Stable filter key — changes when filters/search are applied
  const filterKey = useMemo(
    () => JSON.stringify({ search: committedSearch, ...advancedFilters }),
    [committedSearch, advancedFilters]
  );

  // Fetch first page of tickets for all stages
  useEffect(() => {
    if (!selectedPipelineId) return;

    const pipeline = pipelines.find((p) => p.id === selectedPipelineId);
    const pipelineStages = pipeline?.stages || [];
    if (pipelineStages.length === 0) return;

    let cancelled = false;
    const apiFilters = buildApiFilters(advancedFilters, committedSearch, currentUserId);

    const fetchAll = async () => {
      setInitialLoading(true);
      setError(null);

      try {
        // Single round-trip: ticket-service returns tickets already grouped
        // by stage plus per-stage totals. Replaces the old N-stage parallel
        // fetch that blew the DB connection pool on large tenants.
        const board = await ticketService.getBoard({
          pipelineId: selectedPipelineId,
          perStageLimit: TICKETS_PER_STAGE,
          sortBy: 'updatedAt',
          sortOrder: 'desc',
          ...apiFilters,
        });

        if (cancelled) return;

        // Dedupe across lanes defensively: a ticket only ever belongs in one
        // stage bucket, and we keep it in the bucket matching its own stageId
        // when possible. Prevents the "two cards in two lanes" symptom if the
        // board response ever races with an in-flight stage change.
        const claimed = new Set<string>();
        const newStageStates: Record<string, StageState> = {};
        pipelineStages.forEach((stage) => {
          const rawTickets = board.ticketsByStage[stage.id] || [];
          const tickets = rawTickets.filter((t) => {
            const homeStageId = (t as any).stageId;
            if (homeStageId && homeStageId !== stage.id) return false;
            if (claimed.has(t.id)) return false;
            claimed.add(t.id);
            return true;
          });
          const total = board.totalsByStage[stage.id] || 0;
          newStageStates[stage.id] = {
            tickets,
            total,
            page: 1,
            hasMore: tickets.length < total,
            isLoading: false,
          };
        });

        // Re-apply optimistic moves that haven't been reflected by the API
        // yet (drag → open detail → back can land here before the move
        // commits). Once the API agrees with the pending stage, drop it.
        const pending = pendingMoves.getAll();
        let withPending = newStageStates;
        for (const move of pending) {
          const target = withPending[move.stageId];
          if (!target) continue; // target lane not visible (filters etc.) — skip
          let ticketRef: Ticket | undefined;
          for (const s of Object.values(withPending)) {
            const found = s.tickets.find((t) => t.id === move.ticketId);
            if (found) { ticketRef = found; break; }
          }
          if (ticketRef && (ticketRef as any).stageId === move.stageId) {
            // Backend has caught up — clear the pending entry.
            pendingMoves.clear(move.ticketId);
            continue;
          }
          withPending = placeTicketInStage(withPending, move.ticketId, move.stageId, {
            ticketIfMissing: ticketRef,
            patch: move.status
              ? ({ status: move.status as TicketStatus } as Partial<Ticket>)
              : undefined,
          });
        }
        setStageStates(withPending);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching tickets:', err);
        setError('Failed to load tickets');
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    };

    fetchAll();
    return () => { cancelled = true; };
  }, [selectedPipelineId, pipelines, filterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // WebSocket: Listen for new tickets created externally
  useEffect(() => {
    const unsubscribeCreated = onTicketCreated((data) => {
      const pipelineId = selectedPipelineIdRef.current;
      const currentStages = stagesRef.current;

      // Only add if ticket belongs to current pipeline
      if (!pipelineId || !data.pipelineId || data.pipelineId !== pipelineId) {
        return;
      }

      // Find the stage to add the ticket to
      const targetStageId = data.stageId || currentStages[0]?.id;
      if (!targetStageId) return;

      // Skip if the ticket is already anywhere on the board — this covers the
      // optimistic-create path as well as delayed re-broadcasts that previously
      // inserted a second copy into the target lane.
      const alreadyOnBoard = Object.values(stageStatesRef.current).some((s) =>
        s.tickets.some((t) => t.id === data.id),
      );
      if (alreadyOnBoard) return;

      console.log('[Board] WebSocket: New ticket created', data.ticketNumber);

      setStageStates((prev) =>
        placeTicketInStage(prev, data.id, targetStageId, {
          ticketIfMissing: data as Ticket,
        }),
      );

      toast.info('New ticket', `${data.ticketNumber || 'Ticket'} was created`);
    });

    return () => {
      unsubscribeCreated();
    };
  }, [onTicketCreated, toast]);

  // WebSocket: Listen for ticket updates (stage changes, status changes, etc.)
  useEffect(() => {
    const unsubscribeUpdated = onTicketUpdated((data) => {
      const { ticketId, stageId, previousStageId, stageChanged, ...updates } = data;

      // Handle stage changes - move ticket between columns
      if (stageChanged && stageId) {
        const currentStates = stageStatesRef.current;
        const targetStateExists = !!currentStates[stageId];
        const ticketOnBoard = Object.values(currentStates).some((s) =>
          s.tickets.some((t) => t.id === ticketId),
        );

        // Ticket is somewhere on the board — placeTicketInStage removes every
        // existing copy and inserts one into the target lane, so stale
        // optimistic state can't leave a duplicate behind.
        if (ticketOnBoard) {
          console.log('[Board] WebSocket: Ticket stage changed', ticketId, previousStageId, '->', stageId);
          setStageStates((prev) =>
            placeTicketInStage(prev, ticketId, stageId, {
              patch: { stageId, ...updates } as Partial<Ticket>,
            }),
          );
          return;
        }

        // Ticket isn't on the board (e.g. reopened from Backtrack). Fetch and
        // insert into the target lane if it's part of the current pipeline.
        if (ticketId && targetStateExists) {
          ticketService
            .getTicket(ticketId)
            .then((fullTicket) => {
              if (!fullTicket) return;
              console.log('[Board] WebSocket: Ticket added to target lane after reopen', ticketId, '->', stageId);
              setStageStates((prev) =>
                placeTicketInStage(prev, ticketId, stageId, {
                  ticketIfMissing: fullTicket as Ticket,
                }),
              );
            })
            .catch((err) => console.error('[Board] Failed to fetch ticket for board insert:', err));
          return;
        }
      }

      // Handle other updates (priority, status, assignee, etc.)
      if (ticketId) {
        setStageStates((prev) => {
          const newStates = { ...prev };
          for (const [stageIdKey, state] of Object.entries(newStates)) {
            const ticketIndex = state.tickets.findIndex((t) => t.id === ticketId);
            if (ticketIndex !== -1) {
              const updatedTickets = [...state.tickets];
              updatedTickets[ticketIndex] = { ...updatedTickets[ticketIndex], ...updates };
              newStates[stageIdKey] = { ...state, tickets: updatedTickets };
              break;
            }
          }
          return newStates;
        });
      }
    });

    return () => {
      unsubscribeUpdated();
    };
  }, [onTicketUpdated]);

  // Load more — passes current filters via refs
  const loadMoreForStage = useCallback(async (stageId: string) => {
    const state = stageStatesRef.current[stageId];
    const pipelineId = selectedPipelineIdRef.current;
    if (!state || !state.hasMore || state.isLoading || !pipelineId) return;

    setStageStates((prev) => ({
      ...prev,
      [stageId]: { ...prev[stageId], isLoading: true },
    }));

    try {
      const nextPage = state.page + 1;
      const apiFilters = buildApiFilters(advancedFiltersRef.current, committedSearchRef.current, currentUserId);
      const result = await ticketService.getTickets({
        pipelineId,
        stageId,
        page: nextPage,
        limit: TICKETS_PER_STAGE,
        sortBy: 'updatedAt',
        sortOrder: 'desc',
        ...apiFilters,
      });

      setStageStates((prev) => ({
        ...prev,
        [stageId]: {
          tickets: [...prev[stageId].tickets, ...result.data],
          total: result.total,
          page: nextPage,
          hasMore: result.page * result.limit < result.total,
          isLoading: false,
        },
      }));
    } catch (err: any) {
      console.error(`Error loading more tickets for stage ${stageId}:`, err);
      toast.error(
        'Failed to load more tickets',
        err?.response?.data?.message || err?.response?.data?.error?.message || err?.message || 'Please try again.',
      );
      setStageStates((prev) => ({
        ...prev,
        [stageId]: { ...prev[stageId], isLoading: false },
      }));
    }
  }, [currentUserId, toast]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveTicket(event.active.data.current?.ticket as Ticket);
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveTicket(null);
      if (!over) return;

      const ticketId = active.id as string;
      const overId = over.id as string;
      const pipelineId = selectedPipelineIdRef.current;
      if (!pipelineId) return;

      // Always read fresh state — closure-captured memos can be one render behind.
      const currentStages = stagesRef.current;
      const currentStates = stageStatesRef.current;

      // Resolve the source stage by scanning live state. (If we used a memoized
      // lookup, a mid-render drag could miss the ticket after WebSocket
      // repositioning.)
      let currentStageId: string | undefined;
      let ticket: Ticket | undefined;
      for (const [sid, state] of Object.entries(currentStates)) {
        const found = state.tickets.find((t) => t.id === ticketId);
        if (found) {
          currentStageId = sid;
          ticket = found;
          break;
        }
      }
      if (!currentStageId || !ticket) return;

      // `over.id` can be either a stage id (dropped on an empty column area or
      // column background) or a ticket id (dropped on a card inside a
      // SortableContext). Resolve both into the target stage id.
      const droppedOnStage = currentStages.find((s) => s.id === overId);
      let newStageId: string | undefined;
      if (droppedOnStage) {
        newStageId = overId;
      } else {
        // overId is a ticket id — find the stage that currently holds it.
        for (const [sid, state] of Object.entries(currentStates)) {
          if (state.tickets.some((t) => t.id === overId)) {
            newStageId = sid;
            break;
          }
        }
      }
      if (!newStageId || newStageId === currentStageId) return;

      // Defensive guard against missing state entries for either end.
      if (!currentStates[currentStageId] || !currentStates[newStageId]) {
        console.warn('[Board] Drag aborted: stage state missing', { currentStageId, newStageId });
        return;
      }

      const targetStage = currentStages.find((s) => s.id === newStageId);
      if (targetStage?.wipLimit) {
        const currentCount = currentStates[newStageId]?.total || 0;
        if (currentCount >= targetStage.wipLimit) {
          toast.warning('WIP Limit Reached', `"${targetStage.name}" has reached its limit of ${targetStage.wipLimit} tickets`);
          return;
        }
      }

      // Compute expected status from target stage (optimistic Stage→Status sync)
      const expectedStatus = (targetStage?.mappedStatus || ticket.status) as TicketStatus;
      const sourceStageId = currentStageId; // narrow into closure for rollback

      setStageStates((prev) =>
        placeTicketInStage(prev, ticketId, newStageId!, {
          ticketIfMissing: ticket!,
          patch: { status: expectedStatus } as Partial<Ticket>,
        }),
      );

      // Persist the optimistic move so it survives navigation away from the
      // board (open ticket → click back) until the backend confirms it.
      pendingMoves.set(ticketId, newStageId!, expectedStatus);

      try {
        setIsMoving(true);
        await ticketService.moveToStage(ticketId, pipelineId, newStageId);
        toast.success('Ticket moved', `Moved to ${targetStage?.name || 'new stage'}`);
      } catch (err: any) {
        console.error('[Board] Move failed', { ticketId, from: sourceStageId, to: newStageId, err });
        pendingMoves.clear(ticketId);
        setStageStates((prev) =>
          placeTicketInStage(prev, ticketId, sourceStageId, {
            ticketIfMissing: ticket!,
          }),
        );
        toast.error('Move failed', err?.response?.data?.message || err?.message || 'Failed to move ticket');
      } finally {
        setIsMoving(false);
      }
    },
    [toast]
  );

  const handleTicketClick = useCallback((ticketId: string) => navigate(`/tickets/${ticketId}`), [navigate]);

  const loadMoreCallbacks = useMemo(() => {
    const map: Record<string, () => void> = {};
    for (const stage of stages) map[stage.id] = () => loadMoreForStage(stage.id);
    return map;
  }, [stages, loadMoreForStage]);

  const handleRefresh = useCallback(() => {
    const pipelineId = selectedPipelineIdRef.current;
    if (pipelineId) {
      setSelectedPipelineId(null);
      queueMicrotask(() => setSelectedPipelineId(pipelineId));
    }
  }, []);

  const handleMigrateTickets = useCallback(async () => {
    setIsMigrating(true);
    try {
      const result = await ticketService.assignUnassignedToPipeline();
      if (result.assigned > 0) {
        toast.success('Migration complete', `${result.assigned} tickets assigned to pipeline`);
        handleRefresh();
      } else {
        toast.info('No tickets to migrate', 'All tickets are already assigned to a pipeline');
      }
    } catch (err: any) {
      toast.error('Migration failed', err?.response?.data?.message || err?.message || 'Unknown error');
    } finally {
      setIsMigrating(false);
    }
  }, [toast, handleRefresh]);

  // Toolbar handlers
  const handleSearchSubmit = useCallback((value: string) => {
    setCommittedSearch(value);
  }, []);

  const handleFiltersChange = useCallback((filters: TicketFilters) => {
    setAdvancedFilters(filters);
  }, []);

  const activeFilterCount = useMemo(() => {
    // includeSubTeams only qualifies the Team filter; it is not a filter of its own.
    return Object.entries(advancedFilters).filter(
      ([k, v]) => k !== 'includeSubTeams' && v !== undefined && (Array.isArray(v) ? v.length > 0 : true)
    ).length;
  }, [advancedFilters]);

  const totalTickets = useMemo(() => {
    return Object.values(stageStates).reduce((sum, s) => sum + s.total, 0);
  }, [stageStates]);

  const allTickets = useMemo(() => {
    return Object.values(stageStates).flatMap((s) => s.tickets);
  }, [stageStates]);

  if (pipelinesLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-[var(--co-canvas)] py-4 pr-4">
        <div className="flex flex-col items-center gap-3">
          <Spinner size="lg" />
          <p className="co-mono text-[10.5px] font-semibold uppercase tracking-[.13em] text-[var(--co-ink-3)]">
            Loading pipelines…
          </p>
        </div>
      </div>
    );
  }

  if (pipelinesError || pipelines.length === 0) {
    return (
      <div className="flex h-full items-center justify-center bg-[var(--co-canvas)] py-4 pr-4">
        <div
          className="max-w-sm border border-[var(--co-line)] bg-[var(--co-raised)] px-6 py-7 text-center"
          style={CARD}
        >
          <LayoutGrid className="mx-auto mb-4 h-7 w-7 text-[var(--co-ink-4)]" />
          <h3 className="mb-2 text-[17px] font-semibold tracking-[-.012em] text-[var(--co-ink)]">
            {pipelinesError || 'No pipelines configured'}
          </h3>
          <p className="mb-6 text-[12.5px] leading-[1.6] text-[var(--co-ink-2)]">
            Configure a pipeline to start organizing your tickets on the board.
          </p>
          {/* No pipeline at all: open the create form directly. A load
              error keeps the old "Configure Pipelines" route. */}
          <button
            onClick={() => navigate(pipelinesSettingsTarget(currentTenantId, { create: !pipelinesError }))}
            className={clsx(PRIMARY, 'mx-auto')}
          >
            {pipelinesError ? 'Configure Pipelines' : 'Create your first pipeline'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-[14px] overflow-hidden bg-[var(--co-canvas)] py-4 pr-4">
      {/* Header cluster */}
      <TicketToolbar
        title="Board"
        ticketCount={totalTickets}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onSearchSubmit={handleSearchSubmit}
        filters={advancedFilters}
        onFiltersChange={handleFiltersChange}
        activeFilterCount={activeFilterCount}
        pipelines={pipelines}
        selectedPipelineId={selectedPipelineId}
        onPipelineChange={handlePipelineChange}
        onExportData={handleExport}
        exportColumns={TICKET_EXPORT_COLUMNS}
        viewMode="board"
        onViewToggle={() => navigate('/tickets/list')}
        onRefresh={handleRefresh}
        onNewTicket={() => navigate('/tickets/new')}
        isLoading={initialLoading || isMoving}
      />

      {/* Error banner */}
      {error && (
        <div
          className="flex flex-none items-center gap-3 border border-[var(--co-burgundy-line)] px-4 py-3"
          style={{ ...CARD, background: 'var(--co-burgundy-wash)' }}
        >
          <AlertCircle className="h-4 w-4 flex-none text-[var(--co-burgundy)]" />
          <p className="text-[12.5px] text-[var(--co-burgundy)]">{error}</p>
          <button
            onClick={() => setError(null)}
            className="ml-auto p-1 text-[var(--co-burgundy)] hover:opacity-70"
          >
            <span className="sr-only">Dismiss</span>&times;
          </button>
        </div>
      )}

      {/* Loading */}
      {initialLoading && (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Spinner size="lg" />
            <p className="co-mono text-[10.5px] font-semibold uppercase tracking-[.13em] text-[var(--co-ink-3)]">
              Loading tickets…
            </p>
          </div>
        </div>
      )}

      {/* Board */}
      {/* Top gap is a MARGIN (outside the scrollport), not padding: a
          `padding-top` on the scroller would pin each column's `sticky top-0`
          header at the padding edge, leaving a transparent strip above it
          through which scrolling cards show OVER the header. Margin keeps the
          headers flush to the top so cards scroll strictly BEHIND the opaque
          header. */}
      {!initialLoading && (
        <div className="scrollbar-thin min-h-0 flex-1 overflow-auto pb-2">
          {stages.length === 0 ? (
            <div className="flex h-full items-center justify-center">
              <div
                className="border border-[var(--co-line)] bg-[var(--co-raised)] px-6 py-7 text-center"
                style={CARD}
              >
                <LayoutGrid className="mx-auto mb-4 h-7 w-7 text-[var(--co-ink-4)]" />
                <p className="text-[13.5px] font-semibold text-[var(--co-ink)]">
                  This pipeline has no stages configured.
                </p>
                <p className="mt-1 text-[12.5px] text-[var(--co-ink-2)]">
                  Add stages in Pipeline Settings to start using the board.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Empty board message with migration option */}
              {totalTickets === 0 && !committedSearch && Object.keys(advancedFilters).length === 0 && (
                <div
                  className="mb-[14px] border border-[var(--co-line)] bg-[var(--co-raised)] p-4"
                  style={CARD}
                >
                  <div className="flex items-start gap-3">
                    <RefreshCw className="mt-[2px] h-[18px] w-[18px] flex-none text-[var(--co-brass-ink)]" />
                    <div className="flex-1">
                      <p className="text-[13.5px] font-semibold text-[var(--co-ink)]">
                        No tickets found in this pipeline
                      </p>
                      <p className="mt-1 text-[12.5px] leading-[1.6] text-[var(--co-ink-2)]">
                        You may have tickets that were created before pipeline support. Click below to assign them to this pipeline.
                      </p>
                      <button
                        onClick={handleMigrateTickets}
                        disabled={isMigrating}
                        className={clsx(PRIMARY, 'mt-3 gap-2 disabled:cursor-not-allowed disabled:opacity-50')}
                      >
                        {isMigrating ? (
                          <>
                            <Spinner size="sm" />
                            Migrating...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="h-4 w-4" />
                            Assign unassigned tickets to pipeline
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCorners}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                {/* `min-h-full` + default `items-stretch` makes every column
                    occupy the full visible board height, so short lanes (New,
                    Resolved) have a large drop zone. Without this, drops on
                    empty space below a short column silently miss. */}
                <div className="flex min-h-full gap-[14px]">
                  {stages.map((stage) => {
                    const state = stageStates[stage.id];
                    return (
                      <BoardColumn
                        key={stage.id}
                        stage={stage}
                        tickets={state?.tickets || []}
                        totalCount={state?.total || 0}
                        hasMore={state?.hasMore || false}
                        isLoadingMore={state?.isLoading || false}
                        onTicketClick={handleTicketClick}
                        onLoadMore={loadMoreCallbacks[stage.id]}
                      />
                    );
                  })}
                </div>

                <DragOverlay dropAnimation={{ duration: 200, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)' }}>
                  {activeTicket && (
                    <div className="rotate-[1.5deg] scale-[1.03]">
                      <TicketKanbanCard ticket={activeTicket} onClick={() => {}} isDraggable={false} />
                    </div>
                  )}
                </DragOverlay>
              </DndContext>
            </>
          )}
        </div>
      )}
    </div>
  );
}
